<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    SettingsFieldSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isIMAPEnabled: false,
      address: '',
      port: '',
      login: '',
      password: '',
      isSSLEnabled: true,
      syncDays: 30,
    };
  },
  validations: {
    address: { required },
    port: { required, minLength: minLength(2) },
    login: { required },
    password: { required },
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        imap_enabled,
        imap_address,
        imap_port,
        imap_login,
        imap_password,
        imap_enable_ssl,
      } = this.inbox;
      this.isIMAPEnabled = imap_enabled;
      this.address = imap_address;
      this.port = imap_port;
      this.login = imap_login;
      this.password = imap_password;
      this.isSSLEnabled = imap_enable_ssl;
    },
    async testIMAPConnection() {
      try {
        const result = await this.$store.dispatch(
          'inboxes/testIMAP',
          this.inbox.id
        );
        useAlert(
          result && result.message
            ? result.message
            : this.$t('INBOX_MGMT.IMAP.TEST.SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error && error.message
            ? error.message
            : this.$t('INBOX_MGMT.IMAP.TEST.ERROR_MESSAGE')
        );
      }
    },
    async syncIMAPEmails() {
      try {
        const result = await this.$store.dispatch('inboxes/syncIMAP', {
          inboxId: this.inbox.id,
          days: this.syncDays,
        });
        useAlert(
          result && result.message
            ? result.message
            : this.$t('INBOX_MGMT.IMAP.SYNC.SUCCESS_MESSAGE')
        );
      } catch (error) {
        useAlert(
          error && error.message
            ? error.message
            : this.$t('INBOX_MGMT.IMAP.SYNC.ERROR_MESSAGE')
        );
      }
    },
    async updateInbox() {
      try {
        this.loading = true;
        let payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            imap_enabled: this.isIMAPEnabled,
            imap_address: this.address,
            imap_port: this.port,
            imap_login: this.login,
            imap_password: this.password,
            imap_enable_ssl: this.isSSLEnabled,
          },
        };

        if (!this.isIMAPEnabled) {
          payload.channel.smtp_enabled = false;
        }

        await this.$store.dispatch('inboxes/updateInboxIMAP', payload);
        useAlert(this.$t('INBOX_MGMT.IMAP.EDIT.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message);
      }
    },
  },
};
</script>

<template>
  <SettingsFieldSection
    :label="$t('INBOX_MGMT.IMAP.TITLE')"
    :help-text="$t('INBOX_MGMT.IMAP.NOTE_TEXT')"
    class="[&>div]:!items-start [&>div>label]:mt-1 mb-4"
  >
    <form @submit.prevent="updateInbox">
      <label for="toggle-imap-enable">
        <input
          v-model="isIMAPEnabled"
          type="checkbox"
          class="ltr:mr-1 rtl:ml-1"
          name="toggle-imap-enable"
        />
        {{ $t('INBOX_MGMT.IMAP.TOGGLE_AVAILABILITY') }}
      </label>
      <p>{{ $t('INBOX_MGMT.IMAP.TOGGLE_HELP') }}</p>
      <div v-if="isIMAPEnabled" class="mb-6">
        <woot-input
          v-model="address"
          :class="{ error: v$.address.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.ADDRESS.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.ADDRESS.PLACE_HOLDER')"
          @blur="v$.address.$touch"
        />
        <woot-input
          v-model="port"
          type="number"
          :class="{ error: v$.port.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.PORT.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.PORT.PLACE_HOLDER')"
          @blur="v$.port.$touch"
        />
        <woot-input
          v-model="login"
          :class="{ error: v$.login.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.LOGIN.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.LOGIN.PLACE_HOLDER')"
          @blur="v$.login.$touch"
        />
        <woot-input
          v-model="password"
          :class="{ error: v$.password.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.IMAP.PASSWORD.LABEL')"
          :placeholder="$t('INBOX_MGMT.IMAP.PASSWORD.PLACE_HOLDER')"
          type="password"
          @blur="v$.password.$touch"
        />
        <label for="toggle-enable-ssl">
          <input
            v-model="isSSLEnabled"
            type="checkbox"
            class="ltr:mr-2 rtl:ml-2"
            name="toggle-enable-ssl"
          />
          {{ $t('INBOX_MGMT.IMAP.ENABLE_SSL') }}
        </label>
      </div>
      <div class="flex gap-2 flex-wrap">
        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.IMAP.UPDATE')"
          :is-loading="uiFlags.isUpdatingIMAP"
          :disabled="(v$.$invalid && isIMAPEnabled) || uiFlags.isUpdatingIMAP"
        />
        <NextButton
          v-if="isIMAPEnabled"
          type="button"
          variant="clear"
          color-scheme="secondary"
          :label="$t('INBOX_MGMT.IMAP.TEST.BUTTON_LABEL')"
          :is-loading="uiFlags.isTestingIMAP"
          :disabled="uiFlags.isTestingIMAP || !inbox.id"
          @click="testIMAPConnection"
        />
      </div>
    </form>
    <!-- Sync section -->
    <div v-if="isIMAPEnabled" class="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700">
      <p class="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
        {{ $t('INBOX_MGMT.IMAP.SYNC.TITLE') }}
      </p>
      <p class="text-xs text-slate-500 dark:text-slate-400 mb-3">
        {{ $t('INBOX_MGMT.IMAP.SYNC.DESCRIPTION') }}
      </p>
      <div class="flex gap-3 items-center flex-wrap">
        <div class="flex items-center gap-2">
          <label class="text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
            {{ $t('INBOX_MGMT.IMAP.SYNC.DAYS_LABEL') }}
          </label>
          <select
            v-model="syncDays"
            class="text-sm border border-slate-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
          >
            <option :value="1">1 {{ $t('INBOX_MGMT.IMAP.SYNC.DAY') }}</option>
            <option :value="7">7 {{ $t('INBOX_MGMT.IMAP.SYNC.DAYS') }}</option>
            <option :value="15">15 {{ $t('INBOX_MGMT.IMAP.SYNC.DAYS') }}</option>
            <option :value="30">30 {{ $t('INBOX_MGMT.IMAP.SYNC.DAYS') }}</option>
            <option :value="60">60 {{ $t('INBOX_MGMT.IMAP.SYNC.DAYS') }}</option>
            <option :value="90">90 {{ $t('INBOX_MGMT.IMAP.SYNC.DAYS') }}</option>
          </select>
        </div>
        <NextButton
          type="button"
          color-scheme="primary"
          :label="$t('INBOX_MGMT.IMAP.SYNC.BUTTON_LABEL')"
          :is-loading="uiFlags.isSyncingIMAP"
          :disabled="uiFlags.isSyncingIMAP || !inbox.id"
          @click="syncIMAPEmails"
        />
      </div>
    </div>
  </SettingsFieldSection>
</template>
